<script setup lang="ts">
/**
 * Table cell for monetary amounts stored in minor currency units (e.g. cents).
 *
 * Formats the value via `formatMoney` and applies sign-aware coloring:
 * negative → `--color-money-negative`, positive → `--color-money-positive`,
 * zero → muted. When `amount` is missing or `NaN`, renders `emptyPlaceholder`
 * in muted style. Center-aligned to match other numeric cells.
 *
 * Key props
 *   amount         — value in minor units; omit or `null` to show placeholder.
 *   emptyPlaceholder — text shown when amount is absent (default `–`).
 *   precision      — minor-unit exponent (divisor = 10^precision); `0` renders
 *                    the raw integer without division.
 *   fractionDigits — override for displayed decimal places, independent of
 *                    `precision`.
 *   maxWidth       — forwarded from `DynamicTable` column config.
 */
import { computed } from 'vue'
import { formatMoney } from '@/utils/formatMoney'

const props = withDefaults(
  defineProps<{
    /** Amount in minor currency units (e.g. cents). Omit or null when absent (e.g. empty client); show emptyPlaceholder. */
    amount?: number | null
    /** Shown when amount is absent. Default "–". */
    emptyPlaceholder?: string
    /** ISO 4217 currency code. Default from config. */
    currency?: string
    /** Locale for formatting. Default from config. */
    locale?: string
    /** Decimal places for the minor unit (also the divisor exponent). Default from config. Pass `0` to display the raw amount as an integer (no division by `10^precision`). */
    precision?: number
    /** Override for the displayed fraction digits, independent of `precision`. Use when the divisor and the displayed decimal places need to differ — e.g. `precision: 0` + `fractionDigits: 2` to render a raw `18` as `18.00`. */
    fractionDigits?: number
    /** Horizontal text alignment. Default `center`; use `right` for a trailing money column. */
    align?: 'left' | 'center' | 'right'
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
  }>(),
  {
    emptyPlaceholder: '–',
    currency: undefined,
    locale: undefined,
    precision: undefined,
    fractionDigits: undefined,
    align: 'center',
  },
)

const hasAmount = computed(
  () => props.amount !== undefined && props.amount !== null && !Number.isNaN(props.amount),
)

const result = computed(() =>
  hasAmount.value
    ? formatMoney({
        amount: props.amount!,
        currency: props.currency,
        locale: props.locale,
        precision: props.precision,
        fractionDigits: props.fractionDigits,
      })
    : null,
)

const colorStyle = computed(() => {
  if (!hasAmount.value || !result.value) return {}
  const raw = props.amount!
  if (raw < 0) return { color: 'var(--color-money-negative)' }
  if (raw > 0) return { color: 'var(--color-money-positive)' }
  return { color: 'var(--color-tufte-muted)' }
})
</script>

<template>
  <!--
    Money values are center-aligned across the app for visual consistency
    with other numeric cells (`PositiveIntegerCell`, `NumberCell`,
    `RowIndexCell`). Column headers should set `align: 'center'` in their
    config so the `<th>` label sits over the centered values.
  -->
  <td
    class="font-tufte money-cell"
    :style="{
      ...colorStyle,
      textAlign: props.align,
      ...(props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : {}),
    }"
  >
    <span v-if="result">{{ result.formatted }}</span>
    <span v-else class="text-tufte-muted">{{ emptyPlaceholder }}</span>
  </td>
</template>

<style scoped>
.money-cell {
  text-align: center;
}
</style>
